import React from 'react';
import Axios from 'axios';

import GetPostComments from '@src/api/GetPostComments';
import { generateComment } from '@src/factories';
import { Comment } from '@src/types';

import useAuthenticationTokens from './useAuthenticationTokens';

const limit = 10;
let cancel = Axios.CancelToken.source();

const usePostComments = (id?: string) => {
  const {
    tokens: { accessToken },
  } = useAuthenticationTokens();
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [data, setData] = React.useState<Comment[]>([]);

  const makeRequest = React.useCallback(
    async (newOffset: number): Promise<Comment[]> => {
      if (!id) {
        return Promise.resolve([]);
      }
      const response = await GetPostComments(
        accessToken,
        id,
        newOffset,
        limit,
        {
          cancelToken: cancel.token,
        }
      );
      return response.data.comments.map((comment) => generateComment(comment));
    },
    [accessToken, id]
  );

  const handleRefresh = React.useCallback(async () => {
    try {
      cancel.cancel('Canceling previous requests');
      cancel = Axios.CancelToken.source();
      setRefreshing(true);
      const newData = await makeRequest(0);
      setData(newData);
      setOffset(limit);
      setRefreshing(false);
    } catch (e) {
      setRefreshing(false);
    }
  }, [makeRequest]);

  const handleEndReached = React.useCallback(async () => {
    try {
      if (loading) {
        return;
      }
      setLoading(true);
      const newData = await makeRequest(offset);
      setData([...data, ...newData]);
      setOffset(limit + offset);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  }, [data, loading, makeRequest, offset]);

  React.useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  React.useEffect(() => () => cancel.cancel('Get post comments cancelled'), []);

  return React.useMemo(
    () => ({
      data,
      handleEndReached,
      handleRefresh,
      refreshing,
      loading,
    }),
    [data, handleEndReached, handleRefresh, loading, refreshing]
  );
};

export default usePostComments;
